// Thin wrappers around the PostHog and gtag globals injected in
// web/index.html. Both globals are no-ops if the page hasn't loaded the
// snippets yet (or if they're blocked), so the helpers below never throw.

import type { User } from './api';

type PostHog = {
  capture: (event: string, props?: Record<string, unknown>) => void;
  identify: (id: string, props?: Record<string, unknown>) => void;
  reset: () => void;
  register: (props: Record<string, unknown>) => void;
};

type Gtag = (
  command: 'event' | 'config' | 'js',
  action: string,
  params?: Record<string, unknown>,
) => void;

declare global {
  interface Window {
    posthog?: PostHog;
    gtag?: Gtag;
    dataLayer?: unknown[];
  }
}

export function track(event: string, props?: Record<string, unknown>): void {
  window.posthog?.capture(event, props);
}

export function identifyUser(user: User): void {
  window.posthog?.identify(user.email, {
    name: user.name,
    role: user.role,
    workspace: user.workspace?.domain ?? null,
    is_personal: user.workspace?.is_personal ?? null,
  });
}

export function resetIdentity(): void {
  window.posthog?.reset();
}

// Google Ads custom event helper — fires once per (event, browser session) so
// reloads don't double-count. Uses sessionStorage to match the legacy
// server/index.html implementation.
export function gtagOnce(eventName: string, params?: Record<string, unknown>): void {
  const key = `gtag_${eventName}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    // sessionStorage may be unavailable (private mode, sandboxed iframe). Fire
    // without dedupe rather than dropping the event.
  }
  window.gtag?.('event', eventName, params ?? {});
}

// Google Ads signup conversion. The 'conversion' event name is literal — Ads
// matches by event name + send_to label, not by semantic name like 'sign_up'.
// AW-18084994847 is the conversion ID; the suffix is the conversion-action
// label. Don't change either without updating the Ads conversion action.
const ADS_SIGNUP_SEND_TO = 'AW-18084994847/XxfTCInZrJ0cEJ--zK9D';

export function fireSignupConversion(): void {
  gtagOnce('tgl_signup');
  gtagOnce('conversion', { send_to: ADS_SIGNUP_SEND_TO });
}

// Fires user_signed_up + the Ads conversion when a 'just signed up' marker
// is present in this tab's sessionStorage, then clears it. The marker is
// set by every signup path that lands a fresh user in the SPA — direct
// /auth/register response (RegisterPage), email-verification callback,
// magic-link callback, and OAuth (Google/Microsoft/Zoho) when isNewUser.
// Idempotent: safe to call from top-level App mount on every navigation.
export function flushSignupTracking(): void {
  try {
    if (sessionStorage.getItem('tgl_just_signed_up') !== '1') return;
  } catch {
    return;
  }
  track('user_signed_up');
  fireSignupConversion();
  try { sessionStorage.removeItem('tgl_just_signed_up'); } catch { /* ignore */ }
}
